import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Drawer, AppBar, Toolbar, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Typography, IconButton, Avatar, Menu,
  MenuItem, Divider, Badge, Tooltip, useTheme, useMediaQuery,
  Chip, Button, alpha
} from '@mui/material';
import {
  Dashboard, DirectionsCar, ShoppingCart, People, Warehouse, ShoppingBag,
  Receipt, BarChart, Settings, Menu as MenuIcon, Notifications, Logout,
  Person, Inventory, LocalShipping, AdminPanelSettings, ChevronLeft,
  ChevronRight, BuildCircle, Category, Engineering, AccountBalanceWallet
} from '@mui/icons-material';
import { logout } from '../../store/slices/authSlice';
import { notificationsAPI } from '../../services/api';
import { hasAnyRole } from '../../utils/roles';
//const businessUnit = useSelector(state => state.businessUnit);
//const active = businessUnit?.active || 'cars';

const DRAWER_WIDTH     = 256;
const DRAWER_COLLAPSED = 72;

// ─── Navigation configs per system ────────────────────────────────────────────
const CAR_SALES_NAV = [
  { label: 'Dashboard',       icon: <Dashboard />,         path: '/dashboard' },
  { label: 'Products',        icon: <DirectionsCar />,      path: '/products',        roles: ['admin','manager','sales','storekeeper'] },
  { label: 'Inventory',       icon: <Inventory />,          path: '/inventory',       roles: ['admin','manager','storekeeper'] },
  { label: 'Unit Products',   icon: <Engineering />,        path: '/unit-tracker',   roles: ['admin','manager','storekeeper'] },
  { label: 'Sales',           icon: <ShoppingCart />,       path: '/sales',           roles: ['admin','manager','sales','accountant'] },
  { label: 'Invoices',        icon: <Receipt />,            path: '/invoices',        roles: ['admin','manager','sales','accountant'] },
  { label: 'Customers',       icon: <People />,             path: '/customers',       roles: ['admin','manager','sales'] },
  { label: 'Suppliers',       icon: <LocalShipping />,      path: '/suppliers',       roles: ['admin','manager','storekeeper'] },
  { label: 'Purchase Orders', icon: <ShoppingBag />,        path: '/purchase-orders', roles: ['admin','manager','storekeeper','accountant'] },
  { label: 'Warehouses',      icon: <Warehouse />,          path: '/warehouses',      roles: ['admin','manager','storekeeper'] },
  { label: 'Staffs',          icon: <Person />,             path: '/staffs',          roles: ['admin','manager'] },
  { label: 'Payroll History', icon: <AccountBalanceWallet />, path: '/payroll',        roles: ['admin','manager','accountant'] },
  { label: 'Reports',         icon: <BarChart />,           path: '/reports',         roles: ['admin','manager','accountant'] },
  { label: 'Users',           icon: <AdminPanelSettings />, path: '/users',           roles: ['admin'] },
  { label: 'Settings',        icon: <Settings />,           path: '/settings',        roles: ['admin'] },
];

const SPARE_PARTS_NAV = [
  { label: 'Dashboard',       icon: <Dashboard />,          path: '/sp/dashboard' },
  { label: 'Parts Catalog',   icon: <Category />,           path: '/sp/products',     roles: ['admin','sp_admin','sp_manager','sp_sales','sp_storekeeper'] },
  { label: 'Inventory',       icon: <Inventory />,          path: '/sp/inventory',    roles: ['admin','sp_admin','sp_manager','sp_storekeeper'] },
  { label: 'Orders',          icon: <ShoppingCart />,       path: '/sp/orders',       roles: ['admin','sp_admin','sp_manager','sp_sales','sp_accountant'] },
  { label: 'Customers',       icon: <People />,             path: '/sp/customers',    roles: ['admin','sp_admin','sp_manager','sp_sales'] },
  { label: 'Suppliers',       icon: <LocalShipping />,      path: '/sp/suppliers',    roles: ['admin','sp_admin','sp_manager','sp_storekeeper'] },
  { label: 'Users',           icon: <AdminPanelSettings />, path: '/users',           roles: ['admin'] },
  { label: 'Settings',        icon: <Settings />,           path: '/settings',        roles: ['admin'] },
];

const SYSTEM_CONFIG = {
  car_sales: {
    label: 'Car Sales',
    gradient: 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1a237e 100%)',
    activeColor: '#93c5fd',
    activeBg: 'rgba(147,197,253,0.15)',
    accentGradient: 'linear-gradient(180deg, #60a5fa, #818cf8)',
    logoGradient: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    logoIcon: <DirectionsCar sx={{ color: '#fff', fontSize: 20 }} />,
    nav: CAR_SALES_NAV,
  },
  spare_parts: {
    label: 'Spare Parts',
    gradient: 'linear-gradient(160deg, #1c0a00 0%, #431407 60%, #7c2d12 100%)',
    activeColor: '#fcd34d',
    activeBg: 'rgba(252,211,77,0.15)',
    accentGradient: 'linear-gradient(180deg, #f59e0b, #ef4444)',
    logoGradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    logoIcon: <BuildCircle sx={{ color: '#fff', fontSize: 20 }} />,
    nav: SPARE_PARTS_NAV,
  },
};

// ─── Notification Bell ────────────────────────────────────────────────────────
const NotificationBell = ({ accentColor }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifications, setNotifs] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const fetchNotifs = React.useCallback(async () => {
    try {
      const { data } = await notificationsAPI.getAll({ limit: 10 });
      setNotifs(data.data || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);

  React.useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  const markRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifs(p => p.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(p => Math.max(0, p - 1));
    } catch {}
  };

  const markAll = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifs(p => p.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const dotColor = { info:'#3b82f6', success:'#22c55e', warning:'#f59e0b', error:'#ef4444' };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton onClick={e => { setAnchorEl(e.currentTarget); fetchNotifs(); }}
          sx={{ width: 40, height: 40, color: 'text.secondary',
            '&:hover': { bgcolor: alpha(accentColor || '#1a237e', 0.08) } }}>
          <Badge badgeContent={unreadCount} color="error" max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
            <Notifications sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ elevation: 8, sx: { mt: 1.5, width: 360, maxHeight: 500, borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>Notifications</Typography>
            {unreadCount > 0 && <Chip label={unreadCount} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />}
          </Box>
          {unreadCount > 0 && <Button size="small" onClick={markAll} sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Mark all read</Button>}
        </Box>
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Notifications sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>All caught up!</Typography>
            </Box>
          ) : notifications.map(n => (
            <Box key={n.id} onClick={() => !n.is_read && markRead(n.id)}
              sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5, cursor: n.is_read?'default':'pointer',
                bgcolor: n.is_read ? 'transparent' : alpha('#3b82f6', 0.04),
                borderBottom: '1px solid rgba(0,0,0,0.04)', transition: 'background 0.15s',
                '&:hover': { bgcolor: alpha('#000', 0.03) } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.6, flexShrink: 0,
                bgcolor: n.is_read ? '#d1d5db' : (dotColor[n.type] || '#3b82f6') }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={n.is_read?400:600} sx={{ fontSize: '0.82rem', lineHeight: 1.4 }}>{n.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem', mt: 0.25 }}>{n.message}</Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem', mt: 0.25, display: 'block' }}>{new Date(n.created_at).toLocaleString()}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Menu>
    </>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ open, onClose, variant, collapsed, onToggleCollapse, systemConfig }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useSelector(s => s.auth);
  const isTemp    = variant === 'temporary';
  const width     = isTemp ? DRAWER_WIDTH : (collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH);

  const hasAccess = (roles) => {
    return hasAnyRole(user?.roles || [], roles);
  };

  const cfg = systemConfig;

  return (
    <Drawer variant={variant} open={open} onClose={onClose}
      sx={{ width, flexShrink: 0,
        '& .MuiDrawer-paper': { width, boxSizing: 'border-box', overflowX: 'hidden',
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
          background: cfg.gradient, borderRight: 'none',
          boxShadow: '4px 0 24px rgba(0,0,0,0.18)' }}}>

      {/* Logo */}
      <Box sx={{ height: 64, px: collapsed&&!isTemp?0:2.5,
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed&&!isTemp?'center':'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        {(!collapsed||isTemp) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0,
              background: cfg.logoGradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
              {cfg.logoIcon}
            </Box>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}>DDIG</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{cfg.label}</Typography>
            </Box>
          </Box>
        )}
        {!isTemp && (
          <IconButton onClick={onToggleCollapse} size="small"
            sx={{ color: 'rgba(255,255,255,0.5)', width: 28, height: 28,
              bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 1.5,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' },
              mx: collapsed?'auto':0 }}>
            {collapsed ? <ChevronRight sx={{ fontSize: 18 }} /> : <ChevronLeft sx={{ fontSize: 18 }} />}
          </IconButton>
        )}
      </Box>

      {/* Nav */}
      <List sx={{ px: collapsed&&!isTemp?0.75:1.5, py: 1.5, flexGrow: 1, overflow: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 4 } }}>
        {(!collapsed||isTemp) && (
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem', fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase', px: 1, mb: 0.75, mt: 0.5 }}>
            Navigation
          </Typography>
        )}
        {cfg.nav.filter(item => hasAccess(item.roles)).map(item => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          return (
            <Tooltip key={item.path} title={collapsed&&!isTemp?item.label:''} placement="right">
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isTemp) onClose(); }}
                  sx={{ borderRadius: 2, minHeight: 44,
                    px: collapsed&&!isTemp?0:1.5,
                    justifyContent: collapsed&&!isTemp?'center':'flex-start',
                    position: 'relative', overflow: 'hidden',
                    backgroundColor: active ? cfg.activeBg : 'transparent',
                    '&::before': active ? { content:'""', position:'absolute', left:0, top:'20%', bottom:'20%',
                      width:3, borderRadius:'0 2px 2px 0', background: cfg.accentGradient } : {},
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                    transition: 'all 0.15s' }}>
                  <ListItemIcon sx={{ minWidth: collapsed&&!isTemp?0:38,
                    color: active ? cfg.activeColor : 'rgba(255,255,255,0.55)',
                    '& svg': { fontSize: 20 }, transition: 'color 0.15s' }}>
                    {item.icon}
                  </ListItemIcon>
                  {(!collapsed||isTemp) && (
                    <ListItemText primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active?600:400,
                        color: active ? cfg.activeColor : 'rgba(255,255,255,0.7)',
                        letterSpacing: '-0.01em', noWrap: true }} />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {/* User footer */}
      <Box sx={{ px: collapsed&&!isTemp?0.75:1.5, py: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center',
          gap: collapsed&&!isTemp?0:1.5, justifyContent: collapsed&&!isTemp?'center':'flex-start',
          p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.07)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.11)' }, transition: 'background 0.15s' }}>
          <Avatar sx={{ width: 34, height: 34, flexShrink: 0, fontSize: '0.8rem', fontWeight: 700,
            background: cfg.logoGradient, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            {useSelector(s=>s.auth.user)?.first_name?.[0]}{useSelector(s=>s.auth.user)?.last_name?.[0]}
          </Avatar>
          {(!collapsed||isTemp) && (
            <Box sx={{ minWidth: 0 }}>
              {/* <Typography sx={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }} noWrap>
                {useSelector(s=>s.auth.user)?.first_name} {useSelector(s=>s.auth.user)?.last_name}
              </Typography> */}
              {/* <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', textTransform: 'capitalize' }}>
                {useSelector(s=>s.auth.user)?.roles?.[0] || 'Staff'}
              </Typography> */}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

// ─── Main Layout ──────────────────────────────────────────────────────────────
const Layout = () => {
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen]   = useState(false);
  const [collapsed, setCollapsed]     = useState(false);
  const [userMenu, setUserMenu]       = useState(null);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user }  = useSelector(s => s.auth);
  const { active: activeSystem } = useSelector(s => s.system);
  const systemConfig = SYSTEM_CONFIG[activeSystem] || SYSTEM_CONFIG.car_sales;

  const sidebarWidth = isMobile ? 0 : (collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {isMobile ? (
        <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)}
          variant="temporary" collapsed={false} onToggleCollapse={() => {}}
          systemConfig={systemConfig} />
      ) : (
        <Sidebar open variant="permanent" onClose={() => {}}
          collapsed={collapsed} onToggleCollapse={() => setCollapsed(v => !v)}
          systemConfig={systemConfig} />
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Top Bar */}
        <AppBar position="sticky" elevation={0} sx={{ 
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e2e8f0', 
          color: 'text.primary', 
          zIndex: theme.zIndex.appBar,
          left: isMobile ? 0 : `${sidebarWidth}px`,
          width: isMobile ? '100vw' : `calc(100vw - ${sidebarWidth}px)`,
          transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)'
        }}>
          <Toolbar sx={{ 
            minHeight: '72px !important', 
            px: { xs: 2, sm: 3 }, 
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            {isMobile && (
              <IconButton 
                edge="start" 
                onClick={() => setMobileOpen(true)}
                sx={{ 
                  color: '#64748b', 
                  mr: 1, 
                  '&:hover': { 
                    bgcolor: 'rgba(100, 116, 139, 0.08)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Right side items with better spacing */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
              {/* Notifications */}
              <NotificationBell accentColor={systemConfig.accentGradient?.split(',')[1]?.trim().replace(')', '')} />

              {/* User pill */}
              <Tooltip title="Account">
                <Box 
                  onClick={e => setUserMenu(e.currentTarget)}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    px: 1.5, 
                    py: 0.75, 
                    borderRadius: 3, 
                    cursor: 'pointer',
                    border: '1px solid rgba(226, 232, 240, 0.8)', 
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: 'rgba(248, 250, 252, 0.9)', 
                      borderColor: '#e2e8f0',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <Avatar sx={{ 
                    width: 36, 
                    height: 36, 
                    fontSize: '0.8rem', 
                    fontWeight: 700,
                    background: systemConfig.logoGradient,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}>
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </Avatar>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography sx={{ 
                      fontSize: '0.85rem', 
                      fontWeight: 600, 
                      lineHeight: 1.2, 
                      color: '#0f172a',
                      ml: 0.5
                    }}>
                      {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      color: '#64748b', 
                      textTransform: 'capitalize', 
                      lineHeight: 1 
                    }}>
                      {user?.roles?.[0] || 'Staff'}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* User dropdown */}
        <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ elevation: 8, sx: { mt: 1.5, minWidth: 220, borderRadius: 3,
            border: '1px solid #e2e8f0', overflow: 'hidden' }}}>
          <Box sx={{ px: 2.5, py: 2, bgcolor: '#f8fafc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>{user?.first_name} {user?.last_name}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25 }}>{user?.email}</Typography>
          </Box>
          <Divider />
          {[{ label: 'Profile', icon: <Person sx={{ fontSize: 18 }} />, path: '/profile' },
            { label: 'Settings', icon: <Settings sx={{ fontSize: 18 }} />, path: '/settings' }].map(item => (
            <MenuItem key={item.path} onClick={() => { navigate(item.path); setUserMenu(null); }}
              sx={{ py: 1.25, px: 2.5, gap: 1.5, fontSize: '0.875rem',
                '&:hover': { bgcolor: '#f8fafc' } }}>
              <Box sx={{ color: '#64748b' }}>{item.icon}</Box>{item.label}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={handleLogout}
            sx={{ py: 1.25, px: 2.5, gap: 1.5, fontSize: '0.875rem', color: '#ef4444',
              '&:hover': { bgcolor: alpha('#ef4444', 0.06) } }}>
            <Logout sx={{ fontSize: 18 }} /> Sign Out
          </MenuItem>
        </Menu>

        {/* Page content */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, overflow: 'auto', maxWidth: '100%' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
